import { useState } from 'react'
import { usePostUtilitiesValidatephonenumber } from '@/api/generated/utility/utility'
import type { PhoneApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import {
  VALIDATE_PHONE_NUMBER,
  VALIDATE_PHONE_NUMBER_ERROR,
  VALIDATE_PHONE_NUMBER_SUCCESS,
} from '@/store/member/MemberConstants'
import { apiErrorCodes } from '@/utils/apiHelper'
import { getMemberApiErrorPayload } from './getMemberApiErrorPayload'

/**
 * Validates a phone number for a member.
 *
 * Replaces the `validatePhoneNumber` thunk (the `apiCall`-based POST to
 * /utilities/validatephonenumber).
 *
 * `phoneIsValid` no longer lives on the server member (`members[id].phoneIsValid`). It is now
 * local state owned by this hook and surfaced via the return — the page reads it from here
 * instead of Redux. The value transitions exactly as the old member reducer drove it:
 * `true` on success, `false` on PhoneNumberNotValid / ThirdPartyError, and unchanged on
 * PhoneNumberNotUnique (which only raised an error message). `setPhoneIsValid` lets the page
 * seed the value when entering edit mode (the old `setPhoneValidation` dispatch).
 *
 * The member reducer's phone error mapping (ThirdPartyError / PhoneNumberNotUnique →
 * `state.member.error`) is preserved verbatim by continuing to dispatch the same
 * VALIDATE_PHONE_NUMBER* action types with the API error body as the payload.
 */
export function useValidateMemberPhone(userId: string) {
  const dispatch = useAppDispatch()
  const mutation = usePostUtilitiesValidatephonenumber()
  const [phoneIsValid, setPhoneIsValid] = useState<boolean | null>(null)

  function validate(phone: PhoneApiContract) {
    const extraData = { UserId: userId }
    setPhoneIsValid(null)
    dispatch({ type: VALIDATE_PHONE_NUMBER, extraData })

    mutation.mutate(
      { data: phone },
      {
        onSuccess: () => {
          setPhoneIsValid(true)
          dispatch({
            type: VALIDATE_PHONE_NUMBER_SUCCESS,
            payload: { success: true },
            extraData,
            failed: false,
          })
        },
        onError: (error) => {
          const payload = getMemberApiErrorPayload(error)
          const errorCode =
            payload && typeof payload === 'object' && 'ErrorCode' in payload ? payload.ErrorCode : undefined
          if (errorCode === apiErrorCodes.PHONE_NUMBER_NOT_VALID || errorCode === apiErrorCodes.THIRD_PARTY_ERROR) {
            setPhoneIsValid(false)
          }
          dispatch({
            type: VALIDATE_PHONE_NUMBER_ERROR,
            payload,
            extraData,
            failed: true,
          })
        },
      },
    )
  }

  return { validate, phoneIsValid, setPhoneIsValid, isPending: mutation.isPending }
}
